/**
 * One client.
 *
 * The tab is in the URL so a link to a specific tab can be shared — "look at
 * this client's contacts" is a link, not an instruction to click something
 * after opening it.
 *
 * The Portal access and Services tabs are present only for somebody who may see
 * them — and Services only on an installation with messaging, since a
 * permission for a service that is off is never reported. A tab that opens onto
 * a refusal is an affordance that always fails.
 */

import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Pencil } from 'lucide-react';
import {
  ApiError,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  DetailField,
  DetailGrid,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusChip,
  Tabs,
  api,
  formatDate,
  formatDateTime,
  formatMoney,
  UsageReport,
  todayISO,
  useCan,
  useHasService,
  type Tone,
} from '@connect/web-shared';

import { ClientFormDialog } from './client-form-dialog';
import { ClientSessionsCard } from './client-sessions-card';
import { PortalAccessPanel } from './portal-access-panel';
import { TeamSeatsCard } from './team-seats-card';
import { ClientCustomersTable } from '../customers/client-customers-table';
import { ClientConsentsPanel } from '../legal/client-consents-panel';
import { ClientServicesPanel } from '../messaging/client-services-panel';
import { fetchClientChannels, messagingKeys } from '../messaging/types';

interface ClientDetail {
  public_id: string;
  code: string;
  name: string;
  trading_name: string | null;
  status: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  tax_id: string | null;
  account_manager_name: string | null;
  department_name: string | null;
  currency: string;
  default_hourly_rate: string | null;
  payment_terms_days: number;
  portal_enabled: boolean;
  contact_count: number;
  onboarded_on: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_TONES: Record<string, Tone> = {
  prospect: 'info',
  active: 'success',
  on_hold: 'warning',
  former: 'neutral',
};

export function ClientDetailPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const canViewPortal = useCan('crm.portal_access.view');
  // Both checks, and the service first: `useCan` passes a superuser whatever
  // the permission, so on its own it would show a superuser a Services tab on
  // an installation that does not have the service — every card a refusal.
  const hasMessaging = useHasService('messaging');
  const canViewServices = useCan('messaging.channel.view') && hasMessaging;
  const canViewUsage = useCan('messaging.usage.view') && hasMessaging;
  const canExportUsage = useCan('messaging.usage.export');
  const canViewConsents = useCan('core.consent_record.view');
  const canViewCustomers = useCan('crm.customer.view');
  const requestedTab = params.get('tab') ?? 'overview';
  // A shared link to a tab this person cannot see lands on the overview rather
  // than on a tab that is not there.
  const tab =
    (requestedTab === 'portal' && !canViewPortal) ||
    (requestedTab === 'services' && !canViewServices) ||
    (requestedTab === 'usage' && !canViewUsage) ||
    (requestedTab === 'consents' && !canViewConsents) ||
    (requestedTab === 'contacts' && !canViewCustomers)
      ? 'overview'
      : requestedTab;

  const client = useQuery({
    queryKey: ['crm', 'clients', publicId],
    queryFn: () => api.get<ClientDetail>(`/crm/clients/${publicId}`),
    enabled: Boolean(publicId),
  });

  const services = useQuery({
    queryKey: messagingKeys.channels(publicId ?? ''),
    queryFn: () => fetchClientChannels(publicId ?? ''),
    enabled: Boolean(publicId) && canViewServices,
  });

  if (client.isLoading) return <LoadingState label="Opening the client" />;
  if (client.isError) {
    const error = client.error;
    return (
      <ErrorState
        title={error instanceof ApiError && error.isNotFound ? 'Client not found' : undefined}
        message={
          error instanceof ApiError
            ? error.message
            : 'The client could not be loaded.'
        }
        requestId={error instanceof ApiError ? error.requestId : undefined}
        onRetry={() => void client.refetch()}
      />
    );
  }

  const row = client.data;
  if (!row) return null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={row.name}
        description={row.trading_name && row.trading_name !== row.name ? row.trading_name : undefined}
        breadcrumbs={[{ label: 'Clients', to: '/clients' }, { label: row.code }]}
        actions={
          <>
            <StatusChip value={row.status} tones={STATUS_TONES} />
            <Can permission="crm.client.edit">
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" />
                Edit
              </Button>
            </Can>
          </>
        }
        tabs={
          <Tabs
            value={tab}
            onChange={(next) => setParams((previous) => {
              const search = new URLSearchParams(previous);
              // An open service belongs to the Services tab alone.
              search.delete('service');
              search.delete('section');
              if (next === 'overview') search.delete('tab');
              else search.set('tab', next);
              return search;
            }, { replace: true })}
            items={[
              { id: 'overview', label: 'Overview' },
              ...(canViewCustomers ? [{ id: 'contacts', label: 'Contacts' }] : []),
              ...(canViewPortal ? [{ id: 'portal', label: 'Portal access' }] : []),
              ...(canViewServices
                ? [{ id: 'services', label: 'Services', count: services.data?.length }]
                : []),
              ...(canViewUsage ? [{ id: 'usage', label: 'Usage' }] : []),
              ...(canViewConsents ? [{ id: 'consents', label: 'Consents' }] : []),
            ]}
          />
        }
      />

      {tab === 'overview' ? (
        <div className="space-y-3">
          <Card>
            <CardHeader title="Details" />
            <CardBody>
              <DetailGrid>
                <DetailField label="Code">
                  <span className="font-mono text-xs">{row.code}</span>
                </DetailField>
                <DetailField label="Account manager">{row.account_manager_name}</DetailField>
                <DetailField label="Department">{row.department_name}</DetailField>
                <DetailField label="Email">
                  {row.email ? (
                    <a href={`mailto:${row.email}`} className="text-accent hover:underline">
                      {row.email}
                    </a>
                  ) : null}
                </DetailField>
                <DetailField label="Phone">{row.phone}</DetailField>
                <DetailField label="Website">
                  {row.website ? (
                    <a
                      href={row.website}
                      target="_blank"
                      // `noreferrer` as well as `noopener`: an outbound link to
                      // a client's site should not carry this installation's URL
                      // in the referrer.
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                    >
                      {row.website.replace(/^https?:\/\//, '')}
                      <ExternalLink className="size-3" />
                    </a>
                  ) : null}
                </DetailField>
                <DetailField label="City">{row.city}</DetailField>
                <DetailField label="Country">{row.country}</DetailField>
                <DetailField label="Tax registration">{row.tax_id}</DetailField>
                <DetailField label="Hourly rate">
                  {formatMoney(row.default_hourly_rate, row.currency)}
                </DetailField>
                <DetailField label="Payment terms">{`${row.payment_terms_days} days`}</DetailField>
                <DetailField label="Onboarded">{formatDate(row.onboarded_on)}</DetailField>
                <DetailField label="Portal access">
                  {row.portal_enabled ? 'Enabled' : 'Off'}
                </DetailField>
              </DetailGrid>
            </CardBody>
          </Card>

          {row.address || row.notes ? (
            <div className="grid gap-3 lg:grid-cols-2">
              {row.address ? (
                <Card>
                  <CardHeader title="Address" />
                  <CardBody>
                    <p className="whitespace-pre-line text-sm text-body">{row.address}</p>
                  </CardBody>
                </Card>
              ) : null}
              {row.notes ? (
                <Card>
                  <CardHeader title="Notes" />
                  <CardBody>
                    <p className="whitespace-pre-line text-sm text-body">{row.notes}</p>
                  </CardBody>
                </Card>
              ) : null}
            </div>
          ) : null}

          <p className="text-2xs text-faint">
            Added {formatDateTime(row.created_at)} · last changed {formatDateTime(row.updated_at)}
          </p>
        </div>
      ) : null}

      {/* The client's customers — the contacts it keeps and sends to. Its own
          list, read-only here; the Customer database compares them across
          clients. */}
      {tab === 'contacts' ? (
        <ClientCustomersTable clientPublicId={row.public_id} clientName={row.name} />
      ) : null}

      {tab === 'portal' ? (
        <div className="space-y-4">
          <PortalAccessPanel clientPublicId={row.public_id} clientName={row.name} />
          <ClientSessionsCard clientPublicId={row.public_id} clientName={row.name} />
          <TeamSeatsCard clientPublicId={row.public_id} />
        </div>
      ) : null}

      {tab === 'services' ? (
        <ClientServicesPanel clientPublicId={row.public_id} clientName={row.name} />
      ) : null}

      {tab === 'usage' ? (
        <UsageReport
          basePath={`/messaging/clients/${row.public_id}/reports`}
          queryKey={messagingKeys.reports(row.public_id)}
          groups={['day', 'template', 'sender']}
          canExport={canExportUsage}
          today={todayISO()}
        />
      ) : null}

      {tab === 'consents' ? <ClientConsentsPanel clientPublicId={row.public_id} /> : null}

      <ClientFormDialog
        open={editing}
        publicId={row.public_id}
        onClose={() => setEditing(false)}
      />
    </div>
  );
}
